import { useState, useEffect } from "react";
import { useForm } from "@refinedev/antd";
import type { HttpError } from "@refinedev/core";
import { useGo, useInvalidate } from "@refinedev/core";

import { Alert, Card, Checkbox, Divider, Form, Input, Modal, Select, Space, Switch, Button, Tag, Typography } from "antd";
import { EyeOutlined } from "@ant-design/icons";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const API_URL = (import.meta as any).env?.VITE_API_URL || "http://localhost:8085";

interface StopWordCategory {
  id: string;
  label: string;
  color: string;
  action_type: string;
  description?: string;
}

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  content: string;
  variables?: string[];
  recommended_sentiment?: string[];
  recommended_rating?: number[];
  use_case_note?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface TemplateFormValues {
  name: string;
  description: string;
  category: string;
  content: string;
  recommended_sentiment?: string[];
  recommended_rating?: number[];
  use_case_note?: string;
  is_active: boolean;
}

export const TemplateEditForm = () => {
  const go = useGo();
  const invalidate = useInvalidate();

  const [contentValue, setContentValue] = useState("");
  const [previewVisible, setPreviewVisible] = useState(false);

  // Load categories from API (single source of truth)
  const [categories, setCategories] = useState<StopWordCategory[]>([]);

  useEffect(() => {
    fetch(`${API_URL}/api/v1/stop-word-categories`)
      .then(res => res.json())
      .then(data => setCategories(data.items || []))
      .catch(() => {});
  }, []);

  const { formProps, formLoading, form } = useForm<
    Template,
    HttpError,
    TemplateFormValues
  >({
    redirect: false,
    mutationMode: "pessimistic",
    onMutationSuccess: () => {
      invalidate({ resource: "templates", invalidates: ["resourceAll"] });
      go({
        to: "/templates",
        type: "replace",
        options: { keepQuery: true },
      });
    },
    successNotification: () => ({
      type: "success",
      message: "Успешно",
      description: "Шаблон обновлен",
    }),
    errorNotification: (error) => ({
      type: "error",
      message: "Не удалось сохранить",
      description:
        error?.message || "Проверьте обязательные поля и повторите попытку",
    }),
  });

  // Set initial content value when form data loads
  useEffect(() => {
    if (!formLoading && form) {
      const formValues = form.getFieldsValue() as Partial<Template>;
      if (formValues.content) {
        setContentValue(formValues.content);
      }
    }
  }, [formLoading, form]);

  const handleFinish = async (values: TemplateFormValues) => {
    // Extract variables from content
    const extractedVariables = values.content.match(/\{([^}]+)\}/g)?.map(v => v.slice(1, -1)) || [];
    const uniqueVariables = Array.from(new Set(extractedVariables));

    const payload = {
      ...values,
      variables: uniqueVariables,
      name: values.name.trim(),
      description: values.description.trim(),
    };
    await formProps.onFinish?.(payload);
  };

  // Extract variables from content
  const extractedVariables = contentValue.match(/\{([^}]+)\}/g)?.map(v => v.slice(1, -1)) || [];
  const uniqueVariables = Array.from(new Set(extractedVariables));

  // Preview with sample data
  const getPreviewText = () => {
    let text = contentValue;
    text = text.replace(/\{customer_name\}/g, "Мария");
    text = text.replace(/\{product_name\}/g, "Тушь для ресниц Volume");
    text = text.replace(/\{resolution_text\}/g, "Мы готовы заменить товар или вернуть полную стоимость покупки. Пожалуйста, свяжитесь с нами по телефону 8-800-XXX-XX-XX или напишите на info@art-visage.ru");
    text = text.replace(/\{additional_info\}/g, "Наша тушь содержит натуральные воски и витамин E, гипоаллергенна и протестирована офтальмологами. Полный состав указан на упаковке");
    text = text.replace(/\{questions\}/g, "Не могли бы вы уточнить, какой именно оттенок вы приобрели? Это поможет нам разобраться в ситуации");
    return text;
  };

  return (
    <>
      <Card title="Редактирование шаблона" loading={formLoading} bordered>
        <Form
          {...formProps}
          layout="vertical"
          onFinish={handleFinish}
          autoComplete="off"
        >
          <Form.Item
            label="Название шаблона"
            name="name"
            rules={[{ required: true, message: "Введите название шаблона" }]}
          >
            <Input placeholder="Например: Благодарность за положительный отзыв" size="large" />
          </Form.Item>

          <Form.Item
            label="Описание"
            name="description"
            rules={[{ required: true, message: "Введите описание" }]}
          >
            <TextArea
              rows={2}
              placeholder="Краткое описание шаблона и когда его использовать"
            />
          </Form.Item>

          <Form.Item
            label="Категория"
            name="category"
            tooltip="Тип шаблона для определенной ситуации в общении с клиентами"
          >
            <Select
              placeholder="Выберите категорию"
              size="large"
            >
              {categories.map((cat) => (
                <Select.Option key={cat.id} value={cat.id}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 600, color: cat.color }}>{cat.label}</span>
                    {cat.description && (
                      <>
                        <span style={{ fontSize: 12, color: '#999' }}>—</span>
                        <span style={{ fontSize: 12, color: '#666' }}>{cat.description}</span>
                      </>
                    )}
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Alert
            message="Доступные переменные"
            description={
              <div>
                <Tag color="blue">{'{customer_name}'}</Tag> - Имя покупателя<br/>
                <Tag color="blue">{'{product_name}'}</Tag> - Название товара<br/>
                <Tag color="blue">{'{product_sku}'}</Tag> - Артикул товара<br/>
                <Tag color="blue">{'{brand}'}</Tag> - Бренд<br/>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Склонения автоматически исправляются через LLM
                </Text>
              </div>
            }
            type="info"
            style={{ marginBottom: 16 }}
            showIcon
          />

          <Form.Item
            label="Текст"
            name="content"
            rules={[
              { required: true, message: "Введите текст шаблона" },
              { min: 50, message: "Текст слишком короткий (минимум 50 символов)" }
            ]}
          >
            <TextArea
              rows={12}
              placeholder="Здравствуйте, {customer_name}!&#10;&#10;Спасибо за ваш отзыв о {product_name}..."
              onChange={(e) => setContentValue(e.target.value)}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          {uniqueVariables.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ fontSize: 13 }}>Найденные переменные:</Text>
              <div style={{ marginTop: 8 }}>
                {uniqueVariables.map((v) => (
                  <Tag key={v} color="green" style={{ marginRight: 4 }}>
                    {`{${v}}`}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          <Space style={{ marginBottom: 16 }}>
            <Tag color={contentValue.length < 100 ? 'orange' : contentValue.length > 600 ? 'red' : 'green'}>
              {contentValue.length} символов
            </Tag>
            {contentValue && (
              <a onClick={() => setPreviewVisible(true)}>
                <EyeOutlined /> Предпросмотр
              </a>
            )}
          </Space>

          <Form.Item
            label="Рекомендуется для тональности"
            name="recommended_sentiment"
          >
            <Checkbox.Group>
              <Checkbox value="positive">Позитивные</Checkbox>
              <Checkbox value="neutral">Нейтральные</Checkbox>
              <Checkbox value="negative">Негативные</Checkbox>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item
            label="Рекомендуется для рейтинга"
            name="recommended_rating"
          >
            <Checkbox.Group>
              <Checkbox value={5}>5★</Checkbox>
              <Checkbox value={4}>4★</Checkbox>
              <Checkbox value={3}>3★</Checkbox>
              <Checkbox value={2}>2★</Checkbox>
              <Checkbox value={1}>1★</Checkbox>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item
            label="Примечание для оператора"
            name="use_case_note"
          >
            <TextArea
              rows={2}
              placeholder="Например: Хорошо подходит для кратких положительных отзывов"
            />
          </Form.Item>

          <Form.Item
            label="Активно"
            name="is_active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Space>
            <Button type="primary" htmlType="submit" loading={formLoading}>
              Сохранить
            </Button>
          </Space>
        </Form>
      </Card>

      {/* Preview Modal */}
      <Modal
        title="Предпросмотр шаблона"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={700}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div>
            <Text strong>Пример с подстановкой переменных:</Text>
            <Card size="small" style={{ marginTop: 8, background: '#f5f5f5' }}>
              <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                {getPreviewText()}
              </Paragraph>
            </Card>
          </div>

          <Divider style={{ margin: 0 }} />

          <div>
            <Text strong>Тестовые данные:</Text>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {'{customer_name}'}: "Мария"<br/>
                {'{product_name}'}: "Тушь для ресниц Volume"
              </Text>
            </div>
          </div>

          <Divider style={{ margin: 0 }} />

          <div>
            <Space direction="vertical" size={4}>
              <Text strong>Проверка качества:</Text>
              <Text style={{ fontSize: 13 }}>
                {contentValue.length < 100 && (
                  <Tag color="orange">⚠️ Текст короткий</Tag>
                )}
                {contentValue.length >= 100 && contentValue.length <= 600 && (
                  <Tag color="green">✓ Длина в норме ({contentValue.length} символов)</Tag>
                )}
                {contentValue.length > 600 && (
                  <Tag color="red">⚠️ Текст длинный</Tag>
                )}
              </Text>
              <Text style={{ fontSize: 13 }}>
                {(contentValue.includes('Art-Visage') || contentValue.includes('Estrade')) ? (
                  <Tag color="green">✓ Есть подпись бренда</Tag>
                ) : (
                  <Tag color="orange">⚠️ Нет подписи бренда</Tag>
                )}
              </Text>
              <Text style={{ fontSize: 13 }}>
                {contentValue.startsWith('Добрый день') || contentValue.startsWith('Здравствуйте') ? (
                  <Tag color="green">✓ Есть приветствие</Tag>
                ) : (
                  <Tag color="orange">⚠️ Нет приветствия</Tag>
                )}
              </Text>
            </Space>
          </div>
        </Space>
      </Modal>
    </>
  );
};
